import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FiPlus, FiTrash2, FiEdit2, FiEye, FiEyeOff, FiTag, FiMapPin } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, Button, Modal, Select, ImageUpload, ConfirmDialog, Spinner, Toggle, Badge } from '../../components/common';
import { galleryService } from '../../services/galleryService';
import ReviewFormInput from '../reviews/ReviewFormInput';
import { useModal } from '../../hooks';
import GalleryCategoriesModal from './GalleryCategoriesModal';
import './GalleryPage.css';

const MAX_FILES = 10;
// GIFs skip compression and could exceed the server's upload limit, so photos only.
const ACCEPT = 'image/jpeg,image/png,image/webp';

const EMPTY_UPLOAD = { categoryId: '', title: '', location: '', capacity: '', images: [] };

const GalleryPage = () => {
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');

  const uploadModal = useModal();
  const editModal = useModal();
  const deleteModal = useModal();
  const categoriesModal = useModal();
  const [uploadForm, setUploadForm] = useState(EMPTY_UPLOAD);
  const [editForm, setEditForm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchImages = useCallback(async () => {
    try {
      const response = await galleryService.getAll();
      setImages(response.data.images);
    } catch (error) {
      toast.error('Failed to fetch gallery images');
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await galleryService.getCategories();
      setCategories(response.data);
    } catch (error) {
      toast.error('Failed to fetch gallery categories');
    }
  }, []);

  // Photo counts live on the categories, so most changes refresh both.
  const refreshAll = useCallback(
    () => Promise.all([fetchImages(), fetchCategories()]),
    [fetchImages, fetchCategories]
  );

  useEffect(() => {
    refreshAll().finally(() => setLoading(false));
  }, [refreshAll]);

  // A filter on a category that was just deleted would show an empty grid
  // while the dropdown reads "All Categories".
  useEffect(() => {
    if (categoryFilter && !categories.some((c) => String(c.id) === categoryFilter)) {
      setCategoryFilter('');
    }
  }, [categories, categoryFilter]);

  const categoryOptions = useMemo(
    () => categories.map((c) => ({
      value: String(c.id),
      label: c.isActive ? c.name : `${c.name} (hidden)`,
    })),
    [categories]
  );

  const filterOptions = useMemo(
    () => categories.map((c) => ({ value: String(c.id), label: `${c.name} (${c.imagesCount})` })),
    [categories]
  );

  const hiddenCategoryIds = useMemo(
    () => new Set(categories.filter((c) => !c.isActive).map((c) => c.id)),
    [categories]
  );

  // Filtering is local: the whole gallery is already loaded, and typing should
  // not fire a request per keystroke.
  const visibleImages = useMemo(() => {
    const term = search.trim().toLowerCase();
    return images.filter((img) => {
      if (categoryFilter && String(img.categoryId) !== categoryFilter) return false;
      if (!term) return true;
      return `${img.title} ${img.location} ${img.capacity} ${img.categoryName}`.toLowerCase().includes(term);
    });
  }, [images, search, categoryFilter]);

  const liveCount = images.filter((img) => img.isActive && !hiddenCategoryIds.has(img.categoryId)).length;

  /**
   * Toast why a request failed. A 404 means the image was deleted after this
   * list loaded (another tab or user), so the list is refreshed.
   */
  const reportError = (error, fallback) => {
    const { status, data } = error.response ?? {};
    if (status === 404) {
      toast.error('This image no longer exists. The gallery has been refreshed.');
      editModal.close();
      refreshAll();
    } else if (status === 422 && data?.errors) {
      toast.error(Object.values(data.errors).flat()[0] || fallback);
    } else if (status === 413) {
      toast.error('The upload is too large. Try fewer images at a time.');
    } else if (status !== 500) {
      // 500s are already toasted by the api interceptor.
      toast.error(fallback);
    }
  };

  const openUpload = () => {
    if (categories.length === 0) {
      toast.info('Create a category first, then upload photos into it.');
      categoriesModal.open();
      return;
    }
    // Preselect the category being browsed, so uploads land where the admin is looking.
    setUploadForm({ ...EMPTY_UPLOAD, categoryId: categoryFilter });
    uploadModal.open();
  };

  const closeUpload = () => {
    uploadModal.close();
    setUploadForm(EMPTY_UPLOAD);
  };

  const handleUpload = async () => {
    if (!uploadForm.categoryId) {
      toast.error('Please choose a category');
      return;
    }
    if (!uploadForm.images.length) {
      toast.error('Please select images to upload');
      return;
    }

    setUploading(true);
    try {
      const response = await galleryService.upload({
        categoryId: uploadForm.categoryId,
        title: uploadForm.title.trim(),
        location: uploadForm.location.trim(),
        capacity: uploadForm.capacity.trim(),
        files: uploadForm.images.map((img) => img.file),
      });
      const count = response.data.length;
      toast.success(`${count} ${count === 1 ? 'image' : 'images'} uploaded successfully`);
      closeUpload();
      refreshAll();
    } catch (error) {
      reportError(error, 'Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const openEdit = (image) => {
    setEditForm({
      categoryId: image.categoryId ? String(image.categoryId) : '',
      title: image.title,
      location: image.location,
      capacity: image.capacity,
      isActive: image.isActive,
      // A string URL rather than a File, so the update skips it and the
      // existing photo survives a save that does not touch it.
      image: image.imageUrl,
    });
    editModal.open(image);
  };

  const handleSaveEdit = async () => {
    if (!editForm.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!editForm.categoryId) {
      toast.error('Please choose a category');
      return;
    }
    if (!editForm.image) {
      toast.error('Please choose a photo');
      return;
    }

    setSaving(true);
    try {
      await galleryService.update(editModal.data.id, {
        categoryId: editForm.categoryId,
        title: editForm.title.trim(),
        location: editForm.location.trim(),
        capacity: editForm.capacity.trim(),
        isActive: editForm.isActive,
        // ImageUpload hands back { file, preview } for a new photo.
        image: editForm.image?.file || editForm.image,
      });
      toast.success('Image updated successfully');
      editModal.close();
      refreshAll();
    } catch (error) {
      reportError(error, 'Failed to update image');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (image) => {
    try {
      await galleryService.toggleStatus(image.id);
      setImages((prev) => prev.map((item) =>
        item.id === image.id ? { ...item, isActive: !item.isActive } : item
      ));
      toast.success(image.isActive ? 'Image hidden from the website' : 'Image shown on the website');
    } catch (error) {
      reportError(error, 'Failed to update image');
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await galleryService.delete(deleteModal.data);
      toast.success('Image deleted successfully');
      refreshAll();
    } catch (error) {
      reportError(error, 'Failed to delete image');
    } finally {
      setDeleteLoading(false);
      deleteModal.close();
    }
  };

  const renderEmpty = () => {
    if (categories.length === 0) {
      return (
        <div className="gallery-empty">
          <p>Start by creating a category, such as &ldquo;Solar farms&rdquo; or &ldquo;Our team&rdquo;.</p>
          <Button icon={<FiTag />} onClick={() => categoriesModal.open()}>
            Create Category
          </Button>
        </div>
      );
    }
    if (images.length === 0) {
      return (
        <div className="gallery-empty">
          <p>No images uploaded yet</p>
          <Button icon={<FiPlus />} onClick={openUpload}>
            Upload Images
          </Button>
        </div>
      );
    }
    return (
      <div className="gallery-empty">
        <p>No images match your filters</p>
        <Button variant="ghost" onClick={() => { setSearch(''); setCategoryFilter(''); }}>
          Clear filters
        </Button>
      </div>
    );
  };

  return (
    <div className="gallery-page">
      <Card
        title="Gallery"
        subtitle={`${images.length} images · ${liveCount} on the website · ${categories.length} categories`}
        action={
          <div className="gallery-header-actions">
            <Button variant="secondary" icon={<FiTag />} onClick={() => categoriesModal.open()}>
              Categories
            </Button>
            <Button icon={<FiPlus />} onClick={openUpload}>
              Upload Images
            </Button>
          </div>
        }
      >
        <div className="gallery-toolbar">
          <div className="gallery-search">
            <input
              type="search"
              placeholder="Search by title, location or category..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search images"
            />
          </div>
          <Select
            name="galleryCategoryFilter"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            options={filterOptions}
            placeholder="All Categories"
          />
        </div>

        {loading ? (
          <div className="gallery-loading">
            <Spinner size="large" />
          </div>
        ) : visibleImages.length === 0 ? (
          renderEmpty()
        ) : (
          <div className="gallery-grid">
            {visibleImages.map((image) => {
              const hiddenByCategory = hiddenCategoryIds.has(image.categoryId);
              return (
                <div key={image.id} className={`gallery-item ${image.isActive && !hiddenByCategory ? '' : 'is-hidden'}`}>
                  <img src={image.imageUrl} alt={image.title} loading="lazy" />
                  <div className="gallery-item-badges">
                    {!image.isActive && <Badge variant="warning" size="small">Hidden</Badge>}
                    {image.isActive && hiddenByCategory && <Badge variant="warning" size="small">Category hidden</Badge>}
                    {!image.categoryId && <Badge variant="danger" size="small">No category</Badge>}
                  </div>
                  <div className="gallery-item-overlay">
                    <span className="gallery-item-title">{image.title}</span>
                    <span className="gallery-item-category">
                      {image.categoryName || 'Uncategorized'}
                      {image.location && <> · <FiMapPin aria-hidden="true" /> {image.location}</>}
                    </span>
                  </div>
                  <div className="gallery-item-actions">
                    <button
                      type="button"
                      className="gallery-action-btn"
                      title={image.isActive ? 'Hide from website' : 'Show on website'}
                      aria-label={image.isActive ? 'Hide from website' : 'Show on website'}
                      onClick={() => handleToggle(image)}
                    >
                      {image.isActive ? <FiEye /> : <FiEyeOff />}
                    </button>
                    <button
                      type="button"
                      className="gallery-action-btn edit"
                      title="Edit"
                      aria-label="Edit image"
                      onClick={() => openEdit(image)}
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      type="button"
                      className="gallery-action-btn delete"
                      title="Delete"
                      aria-label="Delete image"
                      onClick={() => deleteModal.open(image.id)}
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Upload Modal */}
      <Modal
        isOpen={uploadModal.isOpen}
        onClose={closeUpload}
        title="Upload Images"
        size="medium"
        footer={
          <>
            <Button variant="ghost" onClick={closeUpload}>
              Cancel
            </Button>
            <Button onClick={handleUpload} loading={uploading}>
              Upload{uploadForm.images.length > 0 ? ` ${uploadForm.images.length}` : ''}
            </Button>
          </>
        }
      >
        <div className="upload-form">
          <Select
            name="uploadCategory"
            label="Category"
            value={uploadForm.categoryId}
            onChange={(e) => setUploadForm({ ...uploadForm, categoryId: e.target.value })}
            options={categoryOptions}
            placeholder="Select category"
            required
          />
          <ReviewFormInput
            name="uploadTitle"
            label="Title (optional)"
            placeholder="e.g. 50 MW Solar Farm"
            value={uploadForm.title}
            onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
          />
          <div className="gallery-form-row">
            <ReviewFormInput
              name="uploadLocation"
              label="Location (optional)"
              placeholder="e.g. Bhuj, Gujarat"
              value={uploadForm.location}
              onChange={(e) => setUploadForm({ ...uploadForm, location: e.target.value })}
            />
            <ReviewFormInput
              name="uploadCapacity"
              label="Capacity / Tag (optional)"
              placeholder="e.g. 50 MW or Site visit"
              value={uploadForm.capacity}
              onChange={(e) => setUploadForm({ ...uploadForm, capacity: e.target.value })}
            />
          </div>
          <ImageUpload
            value={uploadForm.images}
            onChange={(imgs) => setUploadForm((prev) => ({ ...prev, images: imgs }))}
            multiple
            maxFiles={MAX_FILES}
            accept={ACCEPT}
            label={`Select Images (up to ${MAX_FILES})`}
          />
          <p className="gallery-hint">
            Location and capacity apply to every image in this upload. With several images the
            title is numbered (&ldquo;Solar Farm 1&rdquo;, &ldquo;Solar Farm 2&rdquo;); with no
            title, each image is named after its file. Each one can be edited afterwards.
          </p>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={editModal.isOpen}
        onClose={editModal.close}
        title="Edit Image"
        size="medium"
        footer={
          <>
            <Button variant="ghost" onClick={editModal.close}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} loading={saving}>
              Update
            </Button>
          </>
        }
      >
        {editForm && (
          <div className="upload-form">
            <ReviewFormInput
              name="editTitle"
              label="Title"
              value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              required
            />
            <Select
              name="editCategory"
              label="Category"
              value={editForm.categoryId}
              onChange={(e) => setEditForm({ ...editForm, categoryId: e.target.value })}
              options={categoryOptions}
              placeholder="Select category"
              required
            />
            <div className="gallery-form-row">
              <ReviewFormInput
                name="editLocation"
                label="Location (optional)"
                placeholder="e.g. Bhuj, Gujarat"
                value={editForm.location}
                onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
              />
              <ReviewFormInput
                name="editCapacity"
                label="Capacity / Tag (optional)"
                placeholder="e.g. 50 MW or Site visit"
                value={editForm.capacity}
                onChange={(e) => setEditForm({ ...editForm, capacity: e.target.value })}
              />
            </div>
            <ImageUpload
              value={editForm.image}
              onChange={(image) => setEditForm((prev) => ({ ...prev, image }))}
              accept={ACCEPT}
              label="Photo"
            />
            <Toggle
              name="editIsActive"
              checked={editForm.isActive}
              onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
              label="Show on website"
            />
          </div>
        )}
      </Modal>

      <GalleryCategoriesModal
        isOpen={categoriesModal.isOpen}
        onClose={categoriesModal.close}
        categories={categories}
        onChanged={refreshAll}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={handleDelete}
        title="Delete Image"
        message="Are you sure you want to delete this image? This action cannot be undone."
        confirmText="Delete"
        loading={deleteLoading}
      />
    </div>
  );
};

export default GalleryPage;
